import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  PencilIcon,
  ShieldCheckIcon,
  DocumentIcon,
  FolderIcon,
  BuildingOfficeIcon,
  UserGroupIcon,
  UserIcon,
  EyeIcon,
  ArrowPathIcon,
  ClipboardDocumentIcon,
  CameraIcon,
  PrinterIcon,
  FolderPlusIcon,
  DocumentDuplicateIcon,
  PlusCircleIcon,
  ArrowRightCircleIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useAuth } from '../contexts/AuthContext';
import { usePolicyViewHeader } from '../contexts/PolicyViewHeaderContext';
import { apiService } from '../services/api';

const PolicyView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { setPageTitle } = usePageTitle();
  const { user } = useAuth();
  const { setShowEditPolicyButton } = usePolicyViewHeader() ?? {};
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Fetch policy data
  const { data: policyData, isLoading, error } = useQuery({
    queryKey: ['policy', id],
    queryFn: () => apiService.getPolicyById(id!),
    enabled: !!id,
  });

  // Fetch policy recipients
  const { data: policyRecipients, isLoading: recipientsLoading } = useQuery({
    queryKey: ['policyRecipients', id],
    queryFn: () => apiService.getPolicyRecipients(id!),
    enabled: !!id,
  });

  // Fetch user groups for display
  const { data: userGroupsResponse } = useQuery({
    queryKey: ['userGroups'],
    queryFn: () => apiService.getUserGroups(),
  });

  // Fetch contacts for member details
  const { data: contacts } = useQuery({
    queryKey: ['contacts'],
    queryFn: () => apiService.getContacts(),
  });

  // Fetch group members for expanded groups
  const expandedGroupIds = Array.from(expandedGroups);
  const { data: groupMembersData } = useQuery({
    queryKey: ['groupMembers', expandedGroupIds],
    queryFn: async () => {
      if (expandedGroupIds.length === 0) return {};
      const membersData: Record<string, any[]> = {};
      for (const groupId of expandedGroupIds) {
        try {
          const members = await apiService.getUserGroupMembers(groupId);
          membersData[groupId] = members;
        } catch (error) {
          console.error(`Failed to fetch members for group ${groupId}:`, error);
          membersData[groupId] = [];
        }
      }
      return membersData;
    },
    enabled: expandedGroupIds.length > 0,
  });

  // Set page title when policy data is loaded
  React.useEffect(() => {
    if (policyData) {
      setPageTitle('View Policy', policyData.name);
    }
  }, [policyData, setPageTitle]);

  // Tell header whether to show Edit: hide for view-only (admin+system, or user+company)
  useEffect(() => {
    if (!setShowEditPolicyButton) return;
    if (!policyData || !user) {
      setShowEditPolicyButton(true);
      return;
    }
    const isViewOnly =
      (user.role === 'admin' && policyData.type === 'system') ||
      (user.role === 'user' && policyData.type === 'company');
    setShowEditPolicyButton(!isViewOnly);
    return () => {
      setShowEditPolicyButton(true);
    };
  }, [policyData, user, setShowEditPolicyButton]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  if (error || !policyData) {
    return (
      <div className="text-center py-12">
        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Policy not found</h3>
        <p className="mt-1 text-sm text-gray-500">The policy could not be loaded.</p>
        <button
          onClick={() => navigate('/policies')}
          className="mt-4 btn-primary"
        >
          Back to Policies
        </button>
      </div>
    );
  }

  const { settings } = policyData;

  // Helper function to get policy type icon and label
  const getPolicyTypeInfo = (type: string) => {
    switch (type) {
      case 'system':
        return { icon: ShieldCheckIcon, label: 'System Policy', color: 'text-red-600' };
      case 'company':
        return { icon: BuildingOfficeIcon, label: 'Company Policy', color: 'text-blue-600' };
      case 'user':
        return { icon: UserIcon, label: 'User Policy', color: 'text-green-600' };
      default:
        return { icon: DocumentIcon, label: 'Policy', color: 'text-gray-600' };
    }
  };

  const policyTypeInfo = getPolicyTypeInfo(policyData.type);
  const PolicyTypeIcon = policyTypeInfo.icon;

  // Helper function to get asset type icon and label
  const getAssetTypeInfo = (assetType: string) => {
    switch (assetType) {
      case 'file':
        return { icon: DocumentIcon, label: 'File', color: 'text-blue-600' };
      case 'folder':
        return { icon: FolderIcon, label: 'Folder', color: 'text-green-600' };
      default:
        return { icon: DocumentIcon, label: 'Asset', color: 'text-gray-600' };
    }
  };

  const assetTypeInfo = getAssetTypeInfo(policyData.asset_type);
  const AssetTypeIcon = assetTypeInfo.icon;

  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
      }
      return newSet;
    });
  };

  return (
    <div className="space-y-6">
      {/* Policy Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Policy Type */}
        <div className="card">
          <div className="flex items-center space-x-3">
            <PolicyTypeIcon className={`w-6 h-6 ${policyTypeInfo.color}`} />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Policy Type</h3>
              <p className="text-lg font-semibold text-gray-900">{policyTypeInfo.label}</p>
            </div>
          </div>
        </div>

        {/* Asset Type */}
        <div className="card">
          <div className="flex items-center space-x-3">
            <AssetTypeIcon className={`w-6 h-6 ${assetTypeInfo.color}`} />
            <div>
              <h3 className="text-sm font-medium text-gray-500">Asset Type</h3>
              <p className="text-lg font-semibold text-gray-900">{assetTypeInfo.label}</p>
            </div>
          </div>
        </div>

        {/* Status */}
        <div className="card">
          <div className="flex items-center space-x-3">
            {policyData.status === 'active' ? (
              <CheckCircleIcon className="w-6 h-6 text-green-600" />
            ) : (
              <XMarkIcon className="w-6 h-6 text-red-600" />
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-500">Status</h3>
              <p className="text-lg font-semibold text-gray-900 capitalize">{policyData.status}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Recipient Settings */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <UserGroupIcon className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Recipient Settings</h2>
            </div>
            
            <div className="space-y-4">
              {/* Allowed Domains */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Allowed Domains</h3>
                {(settings.recipient_settings?.allowed_domains?.length || 0) > 0 ? (
                  <div className="space-y-2">
                    {(settings.recipient_settings?.allowed_domains || []).map((domain, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 bg-blue-50 rounded-lg">
                        <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">@</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{domain}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No domain restrictions (allows any domain)</p>
                )}
              </div>

              {/* Allowed Subdomains */}
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Allowed Subdomains</h3>
                {(settings.recipient_settings?.allowed_subdomains?.length || 0) > 0 ? (
                  <div className="space-y-2">
                    {(settings.recipient_settings?.allowed_subdomains || []).map((subdomain, index) => (
                      <div key={index} className="flex items-center space-x-2 p-2 bg-green-50 rounded-lg">
                        <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">@</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{subdomain}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">No subdomain restrictions</p>
                )}
              </div>

              {/* Require Approval */}
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Require Approval</span>
                <div className="flex items-center space-x-2">
                  {settings.recipient_settings?.require_approval ? (
                    <CheckCircleIcon className="w-5 h-5 text-green-600" />
                  ) : (
                    <XMarkIcon className="w-5 h-5 text-red-600" />
                  )}
                  <span className="text-sm text-gray-600">
                    {settings.recipient_settings?.require_approval ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Policy Recipients */}
          <div className="card">
            <div className="flex items-center space-x-2 mb-4">
              <UserGroupIcon className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Policy Recipients</h2>
            </div>
            
            {recipientsLoading ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-governate-500 mx-auto"></div>
                <p className="text-sm text-gray-500 mt-2">Loading recipients...</p>
              </div>
            ) : policyRecipients && policyRecipients.length > 0 ? (
              <div className="space-y-3">
                {policyRecipients.map((recipient, index) => {
                  if (recipient.user_group_id) {
                    const userGroup = userGroupsResponse?.groups?.find(g => g.id === recipient.user_group_id);
                    if (!userGroup) return null;
                    
                    const isExpanded = expandedGroups.has(userGroup.id);
                    
                    return (
                      <div key={`group-${index}`} className="border border-gray-200 rounded-lg">
                        <button
                          onClick={() => toggleGroupExpansion(userGroup.id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-3">
                            <UserGroupIcon className="w-5 h-5 text-blue-600" />
                            <div className="text-left">
                              <p className="font-medium text-gray-900">{userGroup.name}</p>
                              <p className="text-sm text-gray-500">{userGroup.description}</p>
                            </div>
                          </div>
                          {isExpanded ? (
                            <ChevronDownIcon className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-gray-50">
                            <div className="p-4">
                              <h4 className="text-sm font-medium text-gray-700 mb-3">Group Members</h4>
                              {(() => {
                                const groupMembers = groupMembersData?.[userGroup.id] || [];
                                const memberContacts = contacts?.filter(contact => 
                                  groupMembers.some((member: any) => member.contact_id === contact.id)
                                ) || [];
                                
                                return memberContacts.length > 0 ? (
                                  <div className="space-y-2">
                                    {memberContacts.map((contact, memberIndex) => (
                                      <div key={memberIndex} className="flex items-center space-x-3 p-2 bg-white rounded-lg">
                                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                          <span className="text-blue-600 font-medium text-sm">
                                            {contact.name ? contact.name.charAt(0).toUpperCase() : '?'}
                                          </span>
                                        </div>
                                        <div className="flex-1">
                                          <p className="text-sm font-medium text-gray-900">{contact.name}</p>
                                          <p className="text-xs text-gray-500">{contact.email}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500">No members found</p>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  } else if (recipient.company_id) {
                    return (
                      <div key={`company-${index}`} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                        <BuildingOfficeIcon className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="font-medium text-gray-900">Company</p>
                          <p className="text-sm text-gray-500">ID: {recipient.company_id}</p>
                        </div>
                      </div>
                    );
                  } else if (recipient.user_id) {
                    return (
                      <div key={`user-${index}`} className="flex items-center space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <UserIcon className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{recipient.user_email || 'User'}</p>
                          {!recipient.user_email && (
                            <p className="text-sm text-gray-500 dark:text-gray-400">ID: {recipient.user_id}</p>
                          )}
                        </div>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <UserGroupIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No recipients configured</h3>
                <p className="text-gray-500">This policy doesn't have any recipients assigned yet.</p>
              </div>
            )}
          </div>

          {/* Access Limitations */}
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <ShieldCheckIcon className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Access Limitations</h2>
              </div>
              
              <div className="space-y-3">
              {settings.access_limitations?.access_count_limitation !== undefined && settings.access_limitations.access_count_limitation !== null ? (
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Access Count Limitation</span>
                    <span className="text-sm text-gray-600">
                      {settings.access_limitations.access_count_limitation} times
                    </span>
                  </div>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Access Count Limitation</span>
                  <span className="text-sm text-gray-600 italic">Unlimited</span>
                </div>
                )}
                
              {settings.access_limitations?.allowed_access_from && settings.access_limitations?.allowed_access_to ? (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Access From</span>
                    <span className="text-sm text-gray-600">
                      {(() => {
                        const date = new Date(settings.access_limitations.allowed_access_from);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day} ${hours}:${minutes}`;
                      })()}
                    </span>
                  </div>
                
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">Access To</span>
                    <span className="text-sm text-gray-600">
                      {(() => {
                        const date = new Date(settings.access_limitations.allowed_access_to);
                        const year = date.getFullYear();
                        const month = String(date.getMonth() + 1).padStart(2, '0');
                        const day = String(date.getDate()).padStart(2, '0');
                        const hours = String(date.getHours()).padStart(2, '0');
                        const minutes = String(date.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day} ${hours}:${minutes}`;
                      })()}
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Time-based Limitations</span>
                  <span className="text-sm text-gray-600 italic">Unlimited</span>
                </div>
              )}

            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* File Permissions - Only show for file assets */}
          {policyData.asset_type === 'file' && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <DocumentIcon className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">File Permissions</h2>
              </div>
              
              <div className="space-y-3">
                {[
                  { key: 'convert_original', label: 'Convert to original', icon: ArrowPathIcon },
                  { key: 'save_changes', label: 'Save changes to secured asset', icon: PencilIcon },
                  { key: 'copy_paste', label: 'Copy/Paste', icon: ClipboardDocumentIcon },
                  { key: 'screen_capture', label: 'Screenshot', icon: CameraIcon },
                  { key: 'screen_watermark', label: 'Screen watermark', icon: EyeIcon },
                  { key: 'print', label: 'Print', icon: PrinterIcon },
                  { key: 'print_watermark', label: 'Print watermark', icon: PrinterIcon },
                  { key: 'share', label: 'Share', icon: UserGroupIcon },
                  { key: 'delete', label: 'Delete', icon: XMarkIcon },
                ].map(({ key, label, icon: Icon }) => {
                  const isAllowed = key === 'screen_watermark'
                    ? settings.screen_watermark
                    : key === 'print_watermark'
                    ? settings.print_watermark
                    : settings.file_permissions?.[key as keyof typeof settings.file_permissions]?.allowed || false;
                  
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Icon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isAllowed ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <XMarkIcon className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-600">
                          {isAllowed ? 'Allowed' : 'Denied'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Folder Permissions - Only show for folder assets */}
          {policyData.asset_type === 'folder' && (
            <div className="card">
              <div className="flex items-center space-x-2 mb-4">
                <FolderIcon className="w-5 h-5 text-green-600" />
                <h2 className="text-lg font-semibold text-gray-900">Folder Permissions</h2>
              </div>
              
              <div className="space-y-3">
                {[
                  { key: 'create', label: 'Create', icon: FolderPlusIcon },
                  { key: 'copy', label: 'Copy', icon: DocumentDuplicateIcon },
                  { key: 'add', label: 'Add', icon: PlusCircleIcon },
                  { key: 'move', label: 'Move', icon: ArrowRightCircleIcon },
                  { key: 'share', label: 'Share', icon: UserGroupIcon },
                  { key: 'delete', label: 'Delete', icon: XMarkIcon },
                ].map(({ key, label, icon: Icon }) => {
                  const isAllowed = settings.folder_permissions?.[key as keyof typeof settings.folder_permissions]?.allowed || false;
                  
                  return (
                    <div key={key} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <Icon className="w-4 h-4 text-gray-600" />
                        <span className="text-sm font-medium text-gray-700">{label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {isAllowed ? (
                          <CheckCircleIcon className="w-5 h-5 text-green-600" />
                        ) : (
                          <XMarkIcon className="w-5 h-5 text-red-600" />
                        )}
                        <span className="text-sm text-gray-600">
                          {isAllowed ? 'Allowed' : 'Denied'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default PolicyView;
